import { SerializedStyles } from '@emotion/react';
import React from 'react';

export enum NodeType {
  Element = 'element',
  Text = 'text',
  Comment = 'comment',
  Variable = 'variable'
}

export interface Element {
  type: NodeType.Element;
  component: string | React.ComponentType;
  props: {
    css?: SerializedStyles;
    'data-hidden'?: string;
  } & React.HTMLProps<unknown>;
  children?: Node[];
  parent?: Element;

  [key: string]: unknown;
}

export interface Text {
  type: NodeType.Text;
  content: string;
  parent?: Element;

  [key: string]: unknown;
}

export interface Comment {
  type: NodeType.Comment;
  content: string;
  parent?: Element;

  [key: string]: unknown;
}

export interface Variable {
  type: NodeType.Variable;
  props: {
    variable: string;
  };
  parent?: Element;

  [key: string]: unknown;
}

export type Node = Element | Text | Comment | Variable;

/**
 * Params received by both {@link Test} and {@link Process} functions of a
 * {@link Processor}.
 */
interface Params<NodeDef extends Node> {
  node: NodeDef;
}

/**
 * Define a processor to be used by the Html2React {@link Component}.
 *
 * @example
 * ```
 * // Modify anchors to open a new tab in case they
 * // point to an external link.
 * const extAnchors = {
 *   name: "external anchors",
 *   priority: 10,
 *   test: ({ node }) =>
 *     node.component === "a" && node.props.href.startsWith("http"),
 *   processor: ({ node }) => {
 *     node.props.target = "_blank";
 *     return node;
 *   }
 * };
 * ```
 */
export interface Processor<NodeDef extends Node = Node> {
  /**
   * Processor name.
   */
  name?: string;

  /**
   * Priority for this processor.
   *
   * @defaultValue 10
   */
  priority?: number;

  /**
   * Evaluate the current node, and if it returns `true`, this node will be
   * passed down to the processor function.
   *
   * @param params - Object of type {@link Params}.
   * @returns A boolean value with the result of the evaluation.
   */
  test: (params: Params<NodeDef>) => boolean;

  /**
   * Modify the current node.
   *
   * @param params - Object of type {@link Params}.
   * @returns The same node modified, a plain object that will be merged with
   * the current node or a falsy value in case the node is deleted.
   */
  processor: (params: Params<NodeDef>) => Partial<Node> | false | null | undefined;

  process?: (params: Params<NodeDef>) => Partial<Node> | false | null | undefined;
}
